/**
 * QUIL — CUSTOM FONT AUTO-LOADER
 *
 * Kamrin drops font files into his OneDrive /Apps/Quil/fonts/ folder; on every
 * launch we scan that folder, register each file as a FontFace, and record the
 * discovered families in settings so every font picker can offer them. This is
 * the only legitimate way to use his licence-locked reading face (it can't be
 * bundled) — mirrors what the Contently app does.
 *
 * Best-effort: offline, a missing folder, or an unreadable file → that font is
 * skipped, never an error. FontFaces don't persist across reloads, so this must
 * run each launch to re-register them (see AuthGate). It is in the eager set: importing it lazily
 * instead made Rolldown fold the shared storage chunk into the entry, over the entry ceiling.
 *
 * Cross-app port 2026-11-03 (contently 127b2e86, 3271556e): a family is always quoted and escaped,
 * for FontFace and for the stored font-family stack, so a file named "Author's Hand" or "1942"
 * loads; one face per family is kept, so a rescan after a file was replaced swaps the face rather
 * than piling a second one on; and a family whose file left the folder leaves document.fonts.
 */

import { QUIL_ONEDRIVE_ROOT } from './auth'
import { listFolderChildren, type FolderChild } from './onedrive'
import { db, type CustomFont } from './db'

const FONT_EXT = /\.(woff2?|ttf|otf)$/i

/** The faces this tab registered, by family, so a rescan can replace or remove them. */
const faces = new Map<string, FontFace>()

/** A family name as a CSS string: quoted, with backslashes and single quotes escaped. */
export function cssFamily(family: string): string {
  return `'${family.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
}

/** Infer a clean family name from a font file name (strips extension + weight words). */
export function familyFromFileName(fileName: string): string {
  return fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[-_](regular|bold|italic|light|medium|semibold|book)$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim() || 'Custom font'
}

/** Load `data` as `family` and make it the family's one face. False when it will not load. */
async function registerFace(family: string, data: ArrayBuffer): Promise<boolean> {
  try {
    const face = new FontFace(cssFamily(family), data)
    await face.load()
    const old = faces.get(family)
    if (old) document.fonts.delete(old)
    document.fonts.add(face)
    faces.set(family, face)
    return true
  } catch {
    return false
  }
}

async function fetchFontBytes(child: FolderChild): Promise<ArrayBuffer | null> {
  if (!child.downloadUrl) return null
  try {
    // downloadUrl is a pre-signed URL — no auth header, no CORS preflight.
    const res = await fetch(child.downloadUrl)
    return res.ok ? await res.arrayBuffer() : null
  } catch {
    return null
  }
}

/**
 * Scan /Apps/Quil/fonts/, register every font file found, and persist the list
 * to settings.customFonts (so the pickers can list them even before the next
 * scan). Returns the fonts that registered successfully. Safe to call repeatedly.
 */
export async function syncOneDriveFonts(): Promise<CustomFont[]> {
  let children: FolderChild[]
  try {
    children = await listFolderChildren(`${QUIL_ONEDRIVE_ROOT}/fonts`, { priority: 'background' })
  } catch {
    return []   // offline / transient — leave any previously-recorded list intact
  }

  const fonts: CustomFont[] = []
  const seen = new Set<string>()
  const listed = new Set<string>()        // every family the folder lists, whether or not it downloaded
  for (const child of children) {
    if (!FONT_EXT.test(child.name)) continue
    const family = familyFromFileName(child.name)
    if (seen.has(family)) continue          // one FontFace per family (first file wins)
    listed.add(family)
    const bytes = await fetchFontBytes(child)
    if ((bytes && (await registerFace(family, bytes))) || faces.has(family)) {
      // Registered now, or a failed download/load that leaves the face already on the page.
      seen.add(family)
      fonts.push({ family, fileName: child.name })
    }
  }
  // A family this tab registered whose file has gone from the folder leaves document.fonts, so the
  // fallback the picker shows is the face the page shows. A file that is listed but failed to download
  // or load this time keeps its face: a transient failure must not drop the reading font mid-session.
  for (const [family, face] of faces) {
    if (!listed.has(family)) {
      document.fonts.delete(face)
      faces.delete(family)
    }
  }

  await db.settings.update(1, { customFonts: fonts })
  return fonts
}
